import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

import { useLocale } from '@src/i18n/LocaleProvider'
import { getLocaleFromCode, isLocaleAllowedOnPath, isValidLocalePath } from '@src/i18n/locale.util'
import useGeolocationStore from '@src/stores/geolocationStore'
import { isExternalBODiLink } from '@src/utils/externalBodiLink'
import { transformExternalBODiLink } from '@src/utils/externalBodiLink'
import { useRouterCacheClear } from '@src/utils/flow-router.client.util'
import { log } from '@src/utils/log.util'

interface NavigateOptionsLocalized extends NavigateOptions {
  target?: '_self' | '_blank'
  hard?: boolean
}

export function useLocalizedRouter() {
  const { locale } = useLocale()
  const router = useRouter()
  const cacheClear = useRouterCacheClear()

  const setCountry = useGeolocationStore((state) => state.setCountry)

  useEffect(() => {
    setCountry(locale.countryCode)
  }, [locale.countryCode, setCountry])

  function getLocalizedUrl(url: string) {
    try {
      if (isExternalBODiLink(url)) return transformExternalBODiLink(url)
      if (url.startsWith('http')) return url
      if (isValidLocalePath(url)) return url

      // Check if the current locale is allowed on this path
      let localePath = locale.path

      // Build the full path to check if the locale is allowed on this path
      const fullPath = `${locale.path}${url}`

      // If the locale is not allowed on this path, use the fallback
      if (!isLocaleAllowedOnPath(locale.code, fullPath)) {
        const fallbackLocaleConfig = getLocaleFromCode(locale.fallbackLocale)
        localePath = fallbackLocaleConfig.path
      }

      return `${localePath}${url}`
    } catch (error) {
      log.error('Error in getLocalizedUrl', { error })
      return url
    }
  }

  return {
    ...router,
    push(url: string, options?: NavigateOptionsLocalized | undefined) {
      if (options?.target === '_blank') {
        window.open(getLocalizedUrl(url), '_blank')
      } else if (cacheClear || options?.hard) {
        log.info(`🔗 Using hard navigation to clear Next.js Router cache: ${url}`)
        window.location.href = getLocalizedUrl(url)
      } else {
        router.push(getLocalizedUrl(url), options)
      }
    },
    replace(url: string, options?: NavigateOptions | undefined) {
      router.replace(getLocalizedUrl(url), options)
    },
  }
}
